import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate } from '@pb/shared';
import { createPresentation, listPresentations } from '../api/presentations';
import { EmptyState, ErrorState, Loading } from '../components/States';
import { TagFilter, TagList } from '../components/Tags';
import { useProject } from '../lib/project';
import { pluralize } from '../lib/text';
import { useResource } from '../lib/useResource';
import { NameDescriptionDialog } from './GroupsPage';

function CreatePresentationDialog({ onClose }: { onClose: () => void }) {
  const { project } = useProject();
  const navigate = useNavigate();
  const [showAgenda, setShowAgenda] = useState(true);
  const [showDividers, setShowDividers] = useState(true);
  return (
    <NameDescriptionDialog
      title="New presentation"
      submitLabel="Create presentation"
      onClose={onClose}
      onSubmit={async (name, description) => {
        const created = await createPresentation(project.id, {
          name,
          description,
          show_agenda: showAgenda,
          show_dividers: showDividers,
        });
        navigate(`/projects/${project.id}/presentations/${created.id}`);
      }}
    >
      <label className="checkbox">
        <input type="checkbox" checked={showAgenda} onChange={(event) => setShowAgenda(event.target.checked)} />
        Agenda slide
      </label>
      <label className="checkbox">
        <input type="checkbox" checked={showDividers} onChange={(event) => setShowDividers(event.target.checked)} />
        Chapter divider slides
      </label>
    </NameDescriptionDialog>
  );
}

export function PresentationsPage() {
  const { project } = useProject();
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const presentations = useResource(
    () => listPresentations(project.id, tagIds, includeRetired),
    [project.id, tagIds.join(','), includeRetired],
  );
  const all = presentations.data ?? [];
  const needle = search.trim().toLowerCase();
  const list = needle
    ? all.filter((p) => p.name.toLowerCase().includes(needle) || p.description.toLowerCase().includes(needle))
    : all;

  return (
    <div className="section">
      <div className="page-header">
        <div className="page-title">
          <h2>Presentations</h2>
          <p className="muted small">Chapters of slide groups, generated into a .pptx on the project template.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            New presentation
          </button>
        </div>
      </div>
      <div className="toolbar">
        <input
          type="search"
          className="input input-search"
          style={{ width: 320 }}
          placeholder="Filter by name"
          aria-label="Filter presentations by name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <label className="checkbox">
          <input type="checkbox" checked={includeRetired} onChange={(event) => setIncludeRetired(event.target.checked)} />
          Show retired
        </label>
      </div>
      <TagFilter selected={tagIds} onChange={setTagIds} />
      {presentations.error ? (
        <ErrorState error={presentations.error} onRetry={() => void presentations.reload()} />
      ) : presentations.data === undefined ? (
        <Loading label="Loading presentations…" />
      ) : list.length === 0 ? (
        all.length === 0 && tagIds.length === 0 ? (
          <EmptyState
            title="No presentations yet"
            action={
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                Create a presentation
              </button>
            }
          >
            A presentation arranges slide groups into chapters. Import slides into the library and build groups first.
          </EmptyState>
        ) : (
          <EmptyState title="No presentations match">Change the filter or the tag selection.</EmptyState>
        )
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Chapters</th>
                <th>Pages</th>
                <th>Tags</th>
                <th>Last change</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="row">
                      <Link to={`/projects/${project.id}/presentations/${p.id}`}>
                        <strong>{p.name}</strong>
                      </Link>
                      {p.retired ? <span className="badge badge-warning">Retired</span> : null}
                    </div>
                    {p.description ? <div className="small muted">{p.description}</div> : null}
                  </td>
                  <td>{pluralize(p.chapter_count, 'chapter')}</td>
                  <td>{p.page_count}</td>
                  <td>
                    <TagList tags={p.tags} />
                  </td>
                  <td className="nowrap">{formatDate(p.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating ? <CreatePresentationDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}
